import React, { useEffect, useMemo, useState } from "react";
import { Elements, IbanElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Loader2, ShieldCheck, CheckCircle2, CreditCard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Stripe SEPA-Abo-Setup. Zwei Schritte:
 *   1. Plan wählen (Flexibel / Flatrate)
 *   2. IBAN eingeben + SEPA-Mandat via Stripe Elements bestätigen
 *   3. Backend startet die Subscription mit 2 Items (Base + Metered)
 */
export default function StripeSubscribe() {
  const [config, setConfig] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const stripePromise = useMemo(
    () => (config?.publishable_key ? loadStripe(config.publishable_key) : null),
    [config?.publishable_key],
  );

  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, meRes] = await Promise.all([
          api.get("/stripe/config"),
          api.get("/stripe/subscription/me"),
        ]);
        setConfig(cfgRes.data);
        setMe(meRes.data);
      } catch (e) {
        toast.error(apiError(e, "Konnte Stripe-Konfiguration nicht laden"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Layout title="Abo" subtitle="Stripe-Abonnement einrichten">
        <div className="flex items-center gap-3 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Wird geladen …
        </div>
      </Layout>
    );
  }

  if (me?.has_subscription) {
    return (
      <Layout title="Abo" subtitle="Aktives Stripe-Abonnement">
        <ActiveSubscriptionCard sub={me} onCancelled={setMe} />
      </Layout>
    );
  }

  if (!stripePromise) {
    return (
      <Layout title="Abo" subtitle="Stripe-Abonnement einrichten">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-800 text-sm">
          Stripe ist noch nicht konfiguriert. Bitte an den Betreiber wenden.
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Abo starten" subtitle="SEPA-Lastschrift · monatliche Abrechnung">
      <Elements stripe={stripePromise}>
        <SubscribeFlow plans={config.plans} onDone={setMe} />
      </Elements>
    </Layout>
  );
}

function ActiveSubscriptionCard({ sub, onCancelled }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm("Abo wirklich zum Periodenende kündigen?")) return;
    setBusy(true);
    try {
      await api.post("/stripe/subscription/cancel", { at_period_end: true });
      toast.success("Kündigung zum Periodenende vorgemerkt");
      const { data } = await api.get("/stripe/subscription/me");
      onCancelled(data);
    } catch (e) {
      toast.error(apiError(e, "Kündigung fehlgeschlagen"));
    } finally {
      setBusy(false);
    }
  };
  const endDate = sub.current_period_end
    ? new Date(sub.current_period_end * 1000).toLocaleDateString("de-DE")
    : null;
  return (
    <div className="max-w-2xl space-y-4" data-testid="stripe-active-sub">
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h2 className="text-lg font-bold text-zinc-900">Aktives Abonnement</h2>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-zinc-500">Plan</div><div className="font-semibold text-zinc-900">{sub.plan || "–"}</div></div>
          <div><div className="text-zinc-500">Status</div><div className="font-semibold text-zinc-900">{sub.status}</div></div>
          <div><div className="text-zinc-500">Nächste Abrechnung</div><div className="font-semibold text-zinc-900">{endDate || "–"}</div></div>
          <div><div className="text-zinc-500">Zahlungsart</div><div className="font-semibold text-zinc-900">SEPA-Lastschrift</div></div>
        </div>
        {sub.cancel_at_period_end && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Abo endet zum {endDate}
          </div>
        )}
        {sub.latest_invoice_url && (
          <a
            href={sub.latest_invoice_url} target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 text-sm font-semibold"
            data-testid="stripe-latest-invoice-link"
          >
            Letzte Rechnung öffnen →
          </a>
        )}
        {!sub.cancel_at_period_end && (
          <button
            onClick={cancel} disabled={busy}
            className="mt-6 h-10 px-4 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 text-sm font-semibold disabled:opacity-50"
            data-testid="stripe-cancel-btn"
          >
            {busy ? "…" : "Zum Periodenende kündigen"}
          </button>
        )}
      </div>
    </div>
  );
}

function SubscribeFlow({ plans, onDone }) {
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  return (
    <div className="max-w-3xl space-y-6">
      <StepIndicator step={step} />
      {step === 1 && (
        <PlanPicker plans={plans} selected={selectedPlan}
          onSelect={(p) => { setSelectedPlan(p); setStep(2); }} />
      )}
      {step === 2 && selectedPlan && (
        <IbanForm
          plan={selectedPlan}
          onBack={() => setStep(1)}
          onSuccess={async () => {
            const { data } = await api.get("/stripe/subscription/me");
            onDone(data);
          }}
        />
      )}
    </div>
  );
}

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-3 text-xs text-zinc-500">
      <span className={step >= 1 ? "text-sky-600 font-semibold" : ""}>1. Plan wählen</span>
      <span className="text-zinc-300">→</span>
      <span className={step >= 2 ? "text-sky-600 font-semibold" : ""}>2. SEPA-Mandat</span>
    </div>
  );
}

function PlanPicker({ plans, selected, onSelect }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="stripe-plan-picker">
      {plans.map((p) => (
        <button
          key={p.id} type="button"
          onClick={() => onSelect(p)}
          className={`text-left rounded-2xl border p-6 transition hover:border-sky-400 hover:shadow-md ${selected?.id === p.id ? "border-sky-500 ring-2 ring-sky-500/30" : "border-zinc-200"} bg-white`}
          data-testid={`stripe-plan-${p.id}`}
        >
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-600 mb-2">{p.name}</div>
          <div className="text-3xl font-bold text-zinc-900">{p.base_amount_eur.toFixed(2)} €<span className="text-sm font-normal text-zinc-500"> / Monat</span></div>
          <div className="text-sm text-zinc-600 mt-2">{p.desc}</div>
          <div className="mt-4 text-xs text-zinc-500">+ {p.per_case_eur.toFixed(2)} € pro erstelltem Gutachten (nutzungsbasiert)</div>
        </button>
      ))}
    </div>
  );
}

function IbanForm({ plan, onBack, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!name.trim()) { toast.error("Bitte Kontoinhaber angeben"); return; }
    setBusy(true);
    try {
      // 1. SetupIntent vom Backend holen
      const { data: si } = await api.post("/stripe/setup-intent");
      // 2. IBAN + Mandat bestätigen
      const iban = elements.getElement(IbanElement);
      const result = await stripe.confirmSepaDebitSetup(si.client_secret, {
        payment_method: {
          sepa_debit: iban,
          billing_details: { name: name.trim(), email: user?.email },
        },
      });
      if (result.error) {
        toast.error(result.error.message || "SEPA-Mandat fehlgeschlagen");
        setBusy(false);
        return;
      }
      const pmId = result.setupIntent?.payment_method;
      if (!pmId) { toast.error("Kein Payment-Method zurückerhalten"); setBusy(false); return; }
      // 3. Subscription starten
      await api.post("/stripe/subscription/start", {
        plan_id: plan.id,
        payment_method_id: pmId,
      });
      toast.success("Abonnement gestartet — willkommen!", {
        description: `Plan: ${plan.name} · monatliche Abrechnung via SEPA`,
      });
      await onSuccess();
    } catch (err) {
      toast.error(apiError(err, "Abo konnte nicht gestartet werden"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-5" data-testid="stripe-iban-form">
      <div className="pb-4 border-b border-zinc-100">
        <div className="text-xs uppercase tracking-wide text-sky-600 font-semibold">Gewählter Plan</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-lg font-bold text-zinc-900">{plan.name}</span>
          <span className="text-sm text-zinc-500">· {plan.base_amount_eur.toFixed(2)} € / Monat + {plan.per_case_eur.toFixed(2)} € / Gutachten</span>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-zinc-700 mb-1">Kontoinhaber</label>
        <input
          value={name} onChange={(e) => setName(e.target.value)} required
          placeholder="Max Mustermann"
          className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition"
          data-testid="stripe-iban-holder"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-zinc-700 mb-1">IBAN</label>
        <div className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white" data-testid="stripe-iban-element">
          <IbanElement options={{ supportedCountries: ["SEPA"], placeholderCountry: "DE",
            style: { base: { fontSize: "14px", color: "#18181b" } } }} />
        </div>
      </div>
      <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-4 text-xs text-zinc-600 leading-relaxed">
        <div className="flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            Mit Absenden erteilen Sie <span className="font-semibold">EasyGutachter Software</span> ein
            SEPA-Lastschriftmandat für den wiederkehrenden Einzug der monatlichen Gebühr.
            Die Bestätigung erfolgt über Stripe. Sie können das Mandat jederzeit widerrufen.
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button" onClick={onBack} disabled={busy}
          className="h-10 px-4 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 text-sm font-semibold disabled:opacity-50"
          data-testid="stripe-back-btn"
        >
          Zurück
        </button>
        <button
          type="submit" disabled={!stripe || busy}
          className="h-10 px-5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition"
          data-testid="stripe-subscribe-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {busy ? "Wird eingerichtet …" : "Kostenpflichtig abschließen"}
        </button>
      </div>
    </form>
  );
}
